import { endOfDay, isValid, startOfDay } from "date-fns";
import { type RefObject, useCallback, useMemo, useRef } from "react";
import { useAnalytics } from "~/contexts/analytics-context";
import { useSession } from "~/contexts/auth-context";
import { useProduct } from "~/contexts/product-context";
import { ALL_MARKETS, useSalesData } from "~/contexts/sales-data-context";
import { isSalesDataLakeEnabled } from "~/shared/sales-data/sales-data-lake";
import { BundleButton } from "./components/bundle";
import { MarketToggle } from "./components/market-toggle";
import { SalesDataExplainer } from "./components/sales-data-explainer";
import { SalesDateRangePicker } from "./components/sales-date-range-picker";
import { PrePublicationBanner } from "./components/pre-publication-banner";
import { SalesHistoryBanner } from "./components/sales-history-banner";
import { SalesLastSyncedNote } from "./components/sales-last-synced-note";
import { SummaryCardsRow } from "./components/summary-cards";
import { TotalSales } from "./components/total-sales";
import { AmazonSales } from "./components/amazon-sales";
import { SalesDataEmptyState } from "./components/sales-data-empty-state";
import { useMetadataEvents } from "./hooks/use-metadata-events";

interface SalesDataTabContentProps {
  isExplainerOpen: boolean;
  onDismissExplainer: () => void;
}

export const SalesDataTabContent = ({
  isExplainerOpen,
  onDismissExplainer,
}: SalesDataTabContentProps) => {
  const { product } = useProduct();
  const { organisation } = useSession();
  const { gaEvent } = useAnalytics();
  const {
    earliestDataDate,
    isFirstDatesLoading,
    firstDatesUnavailable,
    bundleIsbns,
    isBundled,
    markets,
    marketFocus,
    setMarketFocus,
    publishedAt,
    from,
    to,
  } = useSalesData();
  const totalSalesRef = useRef<HTMLDivElement>(null);
  const amazonSalesRef = useRef<HTMLDivElement>(null);

  const salesDataLake = organisation.settings?.salesDataLake;
  const salesDataLakeEnabled = isSalesDataLakeEnabled(salesDataLake);

  const { events } = useMetadataEvents({
    productId: product?.id,
    organisationId: organisation.id,
    from: from ? startOfDay(from) : undefined,
    to: to ? endOfDay(to) : undefined,
    publishedAt,
    enabled: !isBundled && salesDataLakeEnabled,
  });

  const productIsbn = product?.isbn;
  const isbns = useMemo(
    () => (productIsbn ? [productIsbn] : []),
    [productIsbn],
  );
  const summaryIsbns = useMemo(
    () => (isBundled && bundleIsbns.length > 0 ? bundleIsbns : isbns),
    [bundleIsbns, isBundled, isbns],
  );

  const scrollTo = useCallback((ref: RefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleTotalCardClick = useCallback(
    () => scrollTo(totalSalesRef),
    [scrollTo],
  );
  const handleAmazonCardClick = useCallback(
    () => scrollTo(amazonSalesRef),
    [scrollTo],
  );

  const handleMarketChange = useCallback(
    (market: string) => {
      const next =
        market === ALL_MARKETS || markets.includes(market)
          ? market
          : ALL_MARKETS;
      setMarketFocus(next);
      gaEvent({ type: "sales_market_changed", payload: { market: next } });
    },
    [markets, setMarketFocus, gaEvent],
  );

  if (!product) {
    return null;
  }

  // No history ever -> one empty state; failed lookup falls through to windowed sections.
  const hasNoSalesHistory =
    salesDataLakeEnabled &&
    !isFirstDatesLoading &&
    !firstDatesUnavailable &&
    !earliestDataDate;

  const isPublished = Boolean(
    publishedAt &&
      isValid(publishedAt) &&
      startOfDay(publishedAt) <= startOfDay(new Date()),
  );

  return (
    <div className="flex flex-col gap-4 mt-2">
      {isExplainerOpen && <SalesDataExplainer onDismiss={onDismissExplainer} />}
      {salesDataLakeEnabled &&
        !hasNoSalesHistory &&
        publishedAt &&
        !isPublished && (
          <PrePublicationBanner
            bookTitle={product.title}
            publishedAt={publishedAt}
          />
        )}
      {salesDataLakeEnabled && !hasNoSalesHistory && <SalesHistoryBanner />}
      {!salesDataLakeEnabled || hasNoSalesHistory ? (
        <SalesDataEmptyState bookTitle={product.title} />
      ) : (
        // While sales-info is still loading we render the sections anyway (each
        // shows its own skeleton) so their market/Amazon queries start in
        // parallel rather than waiting a round trip behind the sales-info lookup.
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <BundleButton
                workId={product.workId}
                currentIsbn={product.isbn}
              />
              <MarketToggle
                markets={markets}
                marketFocus={marketFocus}
                onChange={handleMarketChange}
              />
            </div>
            <div className="flex items-center gap-3">
              <SalesLastSyncedNote />
              <SalesDateRangePicker />
            </div>
          </div>
          <SummaryCardsRow
            isbns={summaryIsbns}
            bookTitle={product.title}
            onTotalCardClick={handleTotalCardClick}
            onAmazonCardClick={handleAmazonCardClick}
            isPublished={isPublished}
          />
          <div ref={totalSalesRef}>
            <TotalSales
              isbns={isbns}
              bookTitle={product.title}
              isBundled={isBundled}
              bundleIsbns={bundleIsbns}
              workId={product.workId}
              currentIsbn={product.isbn}
              isPublished={isPublished}
              events={events}
            />
          </div>
          <div ref={amazonSalesRef} className="mb-8">
            <AmazonSales
              isbns={isbns}
              bookTitle={product.title}
              publishedAt={product.publishedAt?.toDate()}
              isBundled={isBundled}
              bundleIsbns={bundleIsbns}
              workId={product.workId}
              currentIsbn={product.isbn}
              events={events}
            />
          </div>
        </>
      )}
    </div>
  );
};
